class BasePersonalisationClient {
  constructor(endpoint, useCache = false, debug = false) {
    this.endpoint = endpoint;
    this._br_uid_2 = this.getCookie('_br_uid_2');
    this.useCache = useCache;
    this.debug = debug;
  }

  log(message, data = {}) {
    if (this.debug) {
      console.log(`[Personalisation] ${message}`, data);
    }
  }

  async get(params = {}, cacheKey = null) {
    this.log('Get: Starting request', { params, cacheKey });

    if (this.useCache && cacheKey) {
      this.log('Get: Checking cache', { cacheKey });
      const cachedData = this.getFromCache(cacheKey);
      if (cachedData) {
        this.log('Get: Cache hit', { cacheKey });
        return cachedData;
      }
      this.log('Get: Cache miss', { cacheKey });
    }

    params._br_uid_2 = this._br_uid_2;
    for (let [key, value] of Object.entries(params)) {
      if (value === undefined || value === '') {
        delete params[key];
      }
    }

    const queryParams = new URLSearchParams(params);

    this.log('Get: Fetching from API', {
      url: `${this.endpoint}?${queryParams.toString()}`,
    });

    try {
      const response = await fetch(
        `${this.endpoint}?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await this.handleResponse(response);

      if (this.useCache && cacheKey) {
        this.log('Get: Saving to cache', { cacheKey });
        this.saveToCache(cacheKey, result);
      }

      this.log('Get: Request completed');
      return result;
    } catch (error) {
      console.error('[Personalisation] Get: API Error:', error);
      throw error;
    }
  }

  getFromCache(key) {
    const cachedData = sessionStorage.getItem(key);
    return cachedData ? JSON.parse(cachedData) : null;
  }

  saveToCache(key, data) {
    sessionStorage.setItem(key, JSON.stringify(data));
  }

  async handleResponse(response) {
    try {
      if (response.ok) {
        return await response.json();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'API request failed.');
      }
    } catch (error) {
      throw new Error('Failed to parse JSON response.');
    }
  }

  getCookie(name) {
    let matches = document.cookie.match(
      new RegExp(
        '(?:^|; )' +
          name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') +
          '=([^;]*)'
      )
    );

    return matches ? decodeURIComponent(matches[1]) : undefined;
  }

  getReferrer() {
    return document.referrer || window.location.origin || 'direct';
  }
}

class PersonalisationService extends BasePersonalisationClient {
  async getProducts(q, params = {}) {
    this.log('GetProducts: Starting', { q, params });

    const defaultParams = {
      ref_url: this.getReferrer(),
      request_id: this.generateRandomRequestId(),
      domain_key: 'universalstore_en_perfectstranger',
      user_id: window.theme?.customer?.id ?? '',
      url: window.location.href,
      fl: 'pid,url',
      rows: 32,
      start: 0,
      q: q,
    };

    const mergedParams = { ...defaultParams, ...params };
    this.log('GetProducts: Merged params', { mergedParams });

    const cacheKey = this.useCache
      ? this.generateCacheKey('products', q, mergedParams)
      : null;
    this.log('GetProducts: Generated cache key', { cacheKey });

    const result = await this.get(mergedParams, cacheKey);
    this.log('GetProducts: Completed');
    return result;
  }

  async getCategory(params = {}) {
    this.log('GetCategory: Starting', { params });

    const defaultParams = {
      ref_url: this.getReferrer(),
      request_id: this.generateRandomRequestId(),
      domain_key: 'universalstore_en_perfectstranger',
      user_id: window.theme?.customer?.id ?? '',
      url: window.location.href,
      fl: 'pid',
      rows: 32,
      start: 0,
      view_id: 'AU',
    };

    const mergedParams = { ...defaultParams, ...params };
    this.log('GetCategory: Merged params', { mergedParams });

    const cacheKey = this.useCache
      ? this.generateCacheKey('category', params.q, mergedParams)
      : null;
    this.log('GetPrGetCategoryoducts: Generated cache key', { cacheKey });

    const result = await this.get(mergedParams, cacheKey);
    this.log('GetCategory: Completed');
    return result;
  }

  async getRecommendations(widgetType, styleCode, params = {}) {
    const defaultParams = {
      domain_key: 'universalstore_en_perfectstranger',
      item_id: styleCode,
      url: window.location.href,
      widget_type: widgetType,
      request_id: this.generateRandomRequestId(),
    };

    const mergedParams = { ...defaultParams, ...params };

    return await this.get(mergedParams);
  }

  async getRedirectRules(q) {
    const params = {
      ref_url: this.getReferrer(),
      request_id: this.generateRandomRequestId(),
      domain_key: 'universalstore_en_perfectstranger',
      user_id: window.theme?.customer?.id ?? '',
      url: window.location.href,
      fl: 'pid,url',
      rows: 1,
      start: 0,
      q: q,
      view_id: 'AU',
    };

    return await this.get(params);
  }

  async getAutoSuggestions(q) {
    const params = {
      ref_url: this.getReferrer(),
      request_id: this.generateRandomRequestId(),
      catalog_views: 'universalstore_en_perfectstranger',
      request_type: 'suggest',
      fl: 'pid,title,url,thumb_image,price,sale_price',
      user_id: window.theme?.customer?.id ?? '',
      url: window.location.href,
      q: q,
      view_id: 'AU',
    };

    return await this.get(params);
  }

  async getGlobalRecommendations(widgetId, params = {}) {
    const defaultParams = {
      ref_url: this.getReferrer(),
      request_id: this.generateRandomRequestId(),
      domain_key: 'universalstore_en_perfectstranger',
      url: window.location.href,
      widget_id: widgetId,
    };

    const mergedParams = { ...defaultParams, ...params };

    return await this.get(mergedParams);
  }

  generateCacheKey(prefix, q, params) {
    const relevantParams = ['rows', 'start', 'fl'];
    const paramString = relevantParams
      .map((key) => `${key}:${params[key]}`)
      .join('|');
    const encodedQ = encodeURIComponent(q);
    return `${prefix}_${encodedQ}_${paramString}`;
  }

  generateRandomRequestId() {
    const randomNumber =
      Math.floor(Math.random() * 9000000000000) + 1000000000000;
    return randomNumber;
  }
}

window.PersonalisationService = PersonalisationService;
